import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui";
import { Gavel, CalendarClock, AlertTriangle } from "lucide-react";

// Group-HQ widget: owner-only. Closed Board-Meeting decisions whose follow-up date
// is due now or within the next 7 days. Renders nothing for non-owners (the API
// returns 403 → caught → null) or when there is nothing to follow up on.
export default function AdvisoryFollowupsWidget() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/advisory/follow-ups")
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => { if (active) setData({ due: [], upcoming: [] }); });
    return () => { active = false; };
  }, []);

  if (!data) return null;
  const rows = [...(data.due || []).map((r) => ({ ...r, _due: true })), ...(data.upcoming || []).map((r) => ({ ...r, _due: false }))];
  if (rows.length === 0) return null;

  const fmtDate = (d) => {
    try { return new Date(`${d}T00:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "2-digit", month: "short" }); }
    catch { return d; }
  };

  return (
    <Card className="mb-6" data-testid="advisory-followups-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Gavel className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-medium">{t("hq_advisory_followups")}</h3>
        </div>
        {data.due?.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-medium"
            style={{ color: "#F87171", background: "#F8717122" }} data-testid="advisory-followups-due-count">
            <AlertTriangle className="w-3 h-3" /> {data.due.length}
          </span>
        )}
      </div>
      <div className="space-y-1">
        {rows.map((r) => {
          const color = r._due ? "#F87171" : "#FBBF24";
          return (
            <button key={r.id} onClick={() => navigate("/advisory")} data-testid={`advisory-followup-${r.id}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-surface-hover transition-colors text-left">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
                <CalendarClock className="w-4 h-4" style={{ color }} />
              </span>
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium truncate block">{r.title}</span>
                {r.recommendation && <span className="text-[11px] text-text-secondary">{t(`rec_${r.recommendation}`)}</span>}
              </div>
              <span className="text-xs font-medium tabular-nums shrink-0" style={{ color }}>
                {r._due ? t("hq_followup_due") : fmtDate(r.follow_up_date)}
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
